import { useCallback, useEffect, useState } from 'react';
import { getDefaultPeriodo } from '../components/PeriodoHeader';

/**
 * Fonte unica do periodo (competencia) das telas de Comissionamentos.
 *
 * Por que existe: o periodo viajava no `location.state` de cada `navigate`, e quem
 * esquecia de passar (ou de ler) fazia a tela seguinte voltar ao mes padrao. Eram 8
 * pontos, medidos em 16/09/2026 — entre eles a tela de Regras, que ignorava o valor
 * recebido. Com duas telas decidindo o mesmo corte, a divergencia e questao de tempo
 * (mesma classe do fuso do dashboard de compras, at-100).
 *
 * Escopo: a aba do navegador (`sessionStorage`) — o mes escolhido sobrevive a
 * navegacao e ao F5, e volta ao padrao quando o usuario abre o sistema de novo.
 * Decisao do Victor, 16/09/2026.
 */
export const CHAVE_PERIODO_COMISSIONAMENTOS = 'comissionamentos:periodo';

/** mm-yyyy, o formato que as telas e a API do modulo usam. */
const FORMATO_PERIODO = /^(0[1-9]|1[0-2])-\d{4}$/;

const lerDoStorage = (): string | null => {
  try {
    const guardado = sessionStorage.getItem(CHAVE_PERIODO_COMISSIONAMENTOS);
    // Valor corrompido (edicao manual, versao antiga) volta ao padrao em vez de
    // virar requisicao com periodo invalido.
    return guardado && FORMATO_PERIODO.test(guardado) ? guardado : null;
  } catch {
    // Silencio deliberado: sessionStorage indisponivel (modo privativo, storage
    // cheio) nao e chamada de API e nao deve avisar o usuario — o periodo apenas
    // deixa de sobreviver ao F5.
    return null;
  }
};

const gravarNoStorage = (periodo: string): void => {
  try {
    sessionStorage.setItem(CHAVE_PERIODO_COMISSIONAMENTOS, periodo);
  } catch {
    // Idem: silencio deliberado.
  }
};

export const usePeriodoComissionamentos = (): [string, (periodo: string) => void] => {
  const [periodo, setPeriodoState] = useState<string>(() => lerDoStorage() ?? getDefaultPeriodo());

  // Mantem as telas ja montadas em sincronia quando outra aba troca o periodo.
  useEffect(() => {
    const aoMudarStorage = (e: StorageEvent) => {
      if (e.key !== CHAVE_PERIODO_COMISSIONAMENTOS) return;
      if (e.newValue && FORMATO_PERIODO.test(e.newValue)) setPeriodoState(e.newValue);
    };
    window.addEventListener('storage', aoMudarStorage);
    return () => window.removeEventListener('storage', aoMudarStorage);
  }, []);

  const setPeriodo = useCallback((novo: string) => {
    if (!FORMATO_PERIODO.test(novo)) return;
    gravarNoStorage(novo);
    setPeriodoState(novo);
  }, []);

  return [periodo, setPeriodo];
};

export default usePeriodoComissionamentos;
